import { Link } from "@tanstack/react-router";
import { useLifecycleShell } from "../../lifecycleShellContext";
import type { LifecycleStagePolicy } from "../../lifecycleWorkspace";
import type { LivingMonthFurthestStage } from "../../monthlyProposalModel";
import { lifecycleStageDefinitions } from "../shared/LifecycleStageSurfaces";

const proposalStages = lifecycleStageDefinitions;

type ProposalStagePolicyKey = (typeof proposalStages)[number]["policyKey"];

export type ProposalStageSummary = {
  viewer?: { actorRole?: string };
  stages?: Partial<Record<ProposalStagePolicyKey, LifecycleStagePolicy>>;
};

export function getProposalStageReached(
  proposalStatus?: string,
  furthestStage?: LivingMonthFurthestStage,
) {
  const stage = furthestStage ?? proposalStatus;
  if (stage === "Approved" || stage === "approved" || stage === "client_approved" || stage === "closed") return 5;
  if (stage === "In client review" || stage === "client_delivery" || stage === "client_review") return 5;
  if (stage === "Design" || stage === "design" || stage === "assets_ready") return 4;
  if (stage === "Briefing" || stage === "briefing" || stage === "briefs_ready") return 3;
  if (stage === "Calendar in progress" || stage === "ideation") return 2;
  if (stage === "Not started") return 0;
  return 0;
}

export function ProposalStageNav({
  params,
  pathname = "",
  proposalStatus,
  furthestStage,
  stageSummary,
  stagePolicyLoading = false,
  variant = "shell",
  proposalExists = true,
}: {
  params: { clientSlug: string; month: string };
  pathname?: string;
  proposalStatus?: string;
  furthestStage?: LivingMonthFurthestStage;
  stageSummary?: ProposalStageSummary | null;
  stagePolicyLoading?: boolean;
  variant?: "shell" | "lath";
  proposalExists?: boolean;
}) {
  const lifecycle = useLifecycleShell();
  const clientProjection = lifecycle?.actorRole === "client"
    || stageSummary?.viewer?.actorRole === "client";
  const stages = clientProjection
    ? proposalStages.filter((stage) => stage.policyKey === "clientReview")
    : proposalStages;
  const activeStage = stages.find((stage) => pathname.endsWith(`/${stage.slug}`))?.position ?? 0;
  const statusReached = proposalExists ? getProposalStageReached(proposalStatus, furthestStage) : 0;
  const reached = Math.max(statusReached, activeStage);
  const fullyComplete = furthestStage === "approved"
    || proposalStatus === "Approved"
    || proposalStatus === "approved"
    || proposalStatus === "client_approved"
    || proposalStatus === "closed";

  return (
    <nav
      className={`proposal-stage-nav shared-stage-rail${variant === "lath" ? " lath-stage-chain" : ""}`}
      data-component="lifecycle-stage-rail"
      aria-label={variant === "lath" ? "Month production stages" : "Monthly proposal stages"}
      aria-busy={stagePolicyLoading || undefined}
    >
      {stages.map((stage) => {
        const active = pathname.endsWith(`/${stage.slug}`);
        const policy = stagePolicyLoading ? undefined : stageSummary?.stages?.[stage.policyKey];
        const complete = stagePolicyLoading ? false : policy ? Boolean(policy.complete) : fullyComplete || stage.position < reached;
        const current = stagePolicyLoading ? false : !complete && (active || (!activeStage && stage.position === reached));
        const eligible = stagePolicyLoading ? false : policy ? Boolean(policy.accessible) : proposalExists && stage.position <= reached;
        const statusLabel = stagePolicyLoading
          ? "Loading…"
          : policy
          ? policy.notStarted
            ? "Not started"
            : complete
              ? "Completed"
              : active
                ? eligible ? "Current" : "Blocked"
                : eligible ? "Available" : "Not yet eligible"
          : complete
            ? "Completed"
            : current
              ? "In progress"
              : "Not yet active";
        // Design uses a distinct-piece count; Briefing eligibility includes attention work.
        const count = policy?.complete ? 0 : stage.policyKey === "design"
          ? Number(policy?.pieceCount ?? policy?.eligibleCount ?? 0)
          : Number(policy?.eligibleCount ?? 0) + (stage.policyKey === "briefing" ? 0 : Number(policy?.inProgressCount ?? 0));
        const stateClass = complete ? "is-complete" : current ? "is-current" : "is-pending";
        const contents = <>
          <span className="stage-index">{clientProjection ? 1 : stage.position}</span>
          <span>{stage.label}</span>
          <small title={stagePolicyLoading ? "Stage policy is loading." : policy?.reason}>{statusLabel}{count ? ` · ${count}` : ""}</small>
        </>;
        return eligible ? (
          <Link
            key={stage.slug}
            to={`/client/$clientSlug/$month/${stage.slug}` as never}
            params={params as never}
            className={`${active ? "active " : ""}${stateClass}`.trim()}
            data-stage={stage.stage}
            data-stage-state={complete ? "completed" : current ? "current" : eligible ? "available" : "locked"}
            aria-current={active ? "step" : undefined}
          >
            {contents}
          </Link>
        ) : (
          <span
            key={stage.slug}
            className={`proposal-stage-disabled ${stateClass}${stagePolicyLoading ? " is-loading" : ""}`}
            data-stage={stage.stage}
            data-stage-state={stagePolicyLoading ? "loading" : "locked"}
            aria-disabled="true"
            aria-current={active ? "step" : undefined}
          >
            {contents}
          </span>
        );
      })}
    </nav>
  );
}
